import {
  Component, Input, Output, EventEmitter,
  OnInit, ViewChild, ElementRef, signal
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { IBrandImage } from '../../../../Utils/_models/brand-image.model';
import { environment } from '../../../../environments/environment';
import { CountUpDirective } from "../../../../Utils/_directives/count-up.directive";

@Component({
  selector: 'app-onboarding-image-form',
  standalone: true,
  imports: [FormsModule, CountUpDirective],
  templateUrl: './onboarding-image-form.component.html',
  styleUrl: './onboarding-image-form.component.css',
})
export class OnboardingImageFormComponent implements OnInit {
  @Input() editingImage: IBrandImage | null = null;
  @Input() isSaving = false;
  @Output() saved = new EventEmitter<{
    name: string;
    age: number;
    message: string;
    category: 'onboarding' | 'auth' | 'landing' | 'advertisment' | '';
    imageType: 'circle' | 'square';
    croppedBlob: Blob | null;
    croppedPreviewUrl: string;
    weight?: 'High' | 'Medium' | 'Low' | '';
    order?: number;
  }>();
  @Output() cancelled = new EventEmitter<void>();

  private http = inject(HttpClient);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('cropCanvas') cropCanvas!: ElementRef<HTMLCanvasElement>;

  formName = '';
  formAge: number | null = null;
  formImageType: 'circle' | 'square' = 'circle';
  formMessage = '';
  formCategory: 'onboarding' | 'auth' | 'landing' | 'advertisment' | '' = '';
  showCropper = false;
  cropImgSrc = '';
  croppedBlob: Blob | null = null;
  croppedPreviewUrl = '';
  formWeight: 'High' | 'Medium' | 'Low' | '' = '';
  formOrder = 0;
  skipCropping: boolean = false;
  rawFileToUpload: File | null = null; // To hold raw file reference if
  private img = new Image();
  private isDragging = false;
  private isResizing = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private resizeHandle = '';
  cropBox = { x: 50, y: 50, w: 200, h: 200 };
  private canvasW = 480;
  private canvasH = 320;

  ngOnInit(): void {
    if (this.editingImage) {
      this.formName = this.editingImage.name;
      this.formAge = this.editingImage.age;
      this.formMessage = this.editingImage.message ?? '';
      this.formCategory = (this.editingImage as any).category ?? '';  // ← this line
      this.formImageType = this.editingImage.imageType;
      this.formOrder = this.editingImage.order ?? 0;
      this.formWeight = this.editingImage.weight ?? '';
      this.croppedPreviewUrl = this.editingImage.imageUrl;
      this.cropImgSrc = this.editingImage.imageUrl;
      this.croppedBlob = null;
      this.showCropper = false;
      this.img = new Image();
      this.img.crossOrigin = 'anonymous';

      let initialUrl = this.editingImage.imageUrl;
      if (initialUrl.startsWith('http')) {
        const proxyUrl = `${environment.baseUrl}/gossip/admin/brand-images/proxy?url=${encodeURIComponent(initialUrl)}`;
        this.http.get(proxyUrl, { responseType: 'blob' }).subscribe({
          next: (blob) => {
            this.img.src = URL.createObjectURL(blob);
          },
          error: (err) => {
            console.error('Failed to load initial image for cropping:', err);
            this.img.src = initialUrl;
          }
        });
      } else {
        this.img.src = initialUrl;
      }
    }
  }

  isFormValid(): boolean {
    return !!(this.formName && this.formAge && this.formCategory && this.formImageType && (this.croppedBlob || this.editingImage));
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.rawFileToUpload = file;

    if (this.skipCropping) {
      this.croppedBlob = file;
      this.showCropper = false;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.croppedPreviewUrl = e.target?.result as string;
        this.cropImgSrc = '';
      };
      reader.readAsDataURL(file);

    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropImgSrc = e.target?.result as string;
        this.showCropper = true;
        this.croppedPreviewUrl = '';
        this.croppedBlob = null;
        setTimeout(() => this.initCropper(), 50);
      };
      reader.readAsDataURL(file);
    }
  }

  disableCroppingAction(): void {
    if (this.rawFileToUpload) {
      this.croppedBlob = this.rawFileToUpload;
      this.skipCropping = true;
      this.showCropper = false;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.croppedPreviewUrl = e.target?.result as string;
        this.cropImgSrc = '';
      };
      reader.readAsDataURL(this.rawFileToUpload);
    } else if (this.cropImgSrc) {
      this.skipCropping = true;
      this.showCropper = false;
      this.croppedPreviewUrl = this.cropImgSrc;
      this.croppedBlob = null;
    }
  }

  private initCropper(): void {
    const canvas = this.cropCanvas?.nativeElement;
    if (!canvas) return;
    this.img = new Image();
    this.img.onload = () => {
      this.canvasW = canvas.width;
      this.canvasH = canvas.height;
      const size = Math.min(this.canvasW, this.canvasH) * 0.7;
      this.cropBox = {
        x: (this.canvasW - size) / 2,
        y: (this.canvasH - size) / 2,
        w: size,
        h: this.formImageType === 'circle' ? size : size * 1.25,
      };
      this.drawCropper();
    };
    this.img.src = this.cropImgSrc;
  }

  private drawCropper(): void {
    const canvas = this.cropCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const { x, y, w, h } = this.cropBox;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (this.formImageType === 'circle') {
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
      ctx.restore();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
      ctx.restore();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
    }

    const hs = 10;
    ctx.fillStyle = '#fff';
    [{ cx: x, cy: y }, { cx: x + w, cy: y }, { cx: x, cy: y + h }, { cx: x + w, cy: y + h }]
      .forEach(c => ctx.fillRect(c.cx - hs / 2, c.cy - hs / 2, hs, hs));
  }

  onCanvasMouseDown(event: MouseEvent): void {
    const pos = this.getCanvasPos(event);
    const handle = this.getResizeHandle(pos);
    if (handle) { this.isResizing = true; this.resizeHandle = handle; }
    else if (this.inCropBox(pos)) { this.isDragging = true; }
    this.dragStartX = pos.x;
    this.dragStartY = pos.y;
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDragging && !this.isResizing) return;
    const pos = this.getCanvasPos(event);
    const dx = pos.x - this.dragStartX;
    const dy = pos.y - this.dragStartY;
    this.dragStartX = pos.x;
    this.dragStartY = pos.y;
    const b = this.cropBox;
    const minSize = 40;
    if (this.isDragging) {
      b.x = Math.max(0, Math.min(b.x + dx, this.canvasW - b.w));
      b.y = Math.max(0, Math.min(b.y + dy, this.canvasH - b.h));
    } else if (this.isResizing) {
      if (this.resizeHandle.includes('e')) b.w = Math.max(minSize, b.w + dx);
      if (this.resizeHandle.includes('s')) b.h = Math.max(minSize, b.h + dy);
      if (this.resizeHandle.includes('w')) { b.w = Math.max(minSize, b.w - dx); b.x += dx; }
      if (this.resizeHandle.includes('n')) { b.h = Math.max(minSize, b.h - dy); b.y += dy; }
      if (this.formImageType === 'circle') b.h = b.w;
    }
    this.drawCropper();
  }

  onCanvasMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  private getCanvasPos(event: MouseEvent): { x: number; y: number } {
    const rect = this.cropCanvas.nativeElement.getBoundingClientRect();
    const scaleX = this.cropCanvas.nativeElement.width / rect.width;
    const scaleY = this.cropCanvas.nativeElement.height / rect.height;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  private inCropBox(pos: { x: number; y: number }): boolean {
    const { x, y, w, h } = this.cropBox;
    return pos.x >= x && pos.x <= x + w && pos.y >= y && pos.y <= y + h;
  }

  private getResizeHandle(pos: { x: number; y: number }): string {
    const { x, y, w, h } = this.cropBox;
    const t = 12;
    const near = (a: number, b: number) => Math.abs(a - b) < t;
    let handle = '';
    if (near(pos.y, y)) handle += 'n';
    if (near(pos.y, y + h)) handle += 's';
    if (near(pos.x, x)) handle += 'w';
    if (near(pos.x, x + w)) handle += 'e';
    return handle;
  }

  applyCrop(): void {
    const canvas = this.cropCanvas?.nativeElement;
    if (!canvas) return;
    const { x, y, w, h } = this.cropBox;
    const scaleX = this.img.naturalWidth / this.canvasW;
    const scaleY = this.img.naturalHeight / this.canvasH;
    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(w * scaleX);
    offscreen.height = Math.round(h * scaleY);
    const ctx = offscreen.getContext('2d')!;
    if (this.formImageType === 'circle') {
      ctx.beginPath();
      ctx.ellipse(offscreen.width / 2, offscreen.height / 2, offscreen.width / 2, offscreen.height / 2, 0, 0, Math.PI * 2);
      ctx.clip();
    }
    ctx.drawImage(this.img, Math.round(x * scaleX), Math.round(y * scaleY), Math.round(w * scaleX), Math.round(h * scaleY), 0, 0, offscreen.width, offscreen.height);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      this.croppedBlob = blob;
      this.croppedPreviewUrl = URL.createObjectURL(blob);
      this.showCropper = false;
    }, 'image/png');
  }

  reCrop(): void {
    this.croppedPreviewUrl = '';
    this.croppedBlob = null;
    this.showCropper = true;
    setTimeout(() => {
      const canvas = this.cropCanvas?.nativeElement;
      if (!canvas) return;
      this.canvasW = canvas.width;
      this.canvasH = canvas.height;
      const size = Math.min(this.canvasW, this.canvasH) * 0.7;
      this.cropBox = {
        x: (this.canvasW - size) / 2,
        y: (this.canvasH - size) / 2,
        w: size,
        h: this.formImageType === 'circle' ? size : size * 1.25,
      };
      if (this.img.complete && this.img.naturalWidth) {
        this.drawCropper();
      } else {
        this.img = new Image();
        this.img.crossOrigin = 'anonymous';
        this.img.onload = () => {
          this.drawCropper();
        };

        let srcToLoad = this.cropImgSrc;
        if (srcToLoad.startsWith('http')) {
          const proxyUrl = `${environment.baseUrl}/gossip/admin/brand-images/proxy?url=${encodeURIComponent(srcToLoad)}`;
          this.http.get(proxyUrl, { responseType: 'blob' }).subscribe({
            next: (blob) => {
              this.img.src = URL.createObjectURL(blob);
            },
            error: (err) => {
              console.error('Failed to load image for cropping:', err);
              // Fallback
              this.img.src = srcToLoad;
            }
          });
        } else {
          this.img.src = srcToLoad;
        }
      }
    }, 50);
  }

  onImageTypeChange(): void {
    if (this.showCropper && this.img.src) {
      const size = Math.min(this.canvasW, this.canvasH) * 0.7;
      this.cropBox = {
        x: (this.canvasW - size) / 2,
        y: (this.canvasH - size) / 2,
        w: size,
        h: this.formImageType === 'circle' ? size : size * 1.25,
      };
      this.drawCropper();
    }
  }

  save(): void {
    if (!this.isFormValid()) return;
    this.saved.emit({
      name: this.formName,
      age: this.formAge!,
      message: this.formMessage,
      category: this.formCategory as 'onboarding' | 'auth' | 'landing' | 'advertisment',
      imageType: this.formImageType,
      croppedBlob: this.croppedBlob,
      croppedPreviewUrl: this.croppedPreviewUrl,
      order : this.formOrder ?? 0,
      weight : this.formWeight ?? '',
    });
  }

  openImageInNewTab(): void {
    if (!this.croppedPreviewUrl) return;
    let absoluteUrl = this.croppedPreviewUrl;
    if (!absoluteUrl.startsWith('http') && !absoluteUrl.startsWith('data:') && !absoluteUrl.startsWith('blob:')) {
      const base = window.location.origin;
      const path = absoluteUrl.startsWith('/') ? absoluteUrl : '/' + absoluteUrl;
      absoluteUrl = base + path;
    }
    window.open(absoluteUrl, '_blank');
  }

  cancel(): void {
    this.cancelled.emit();
  }

  removeImage(): void {
    // 1. Reset all image preview and source data
    this.croppedPreviewUrl = '';
    this.cropImgSrc = '';
    this.showCropper = false;

    // 2. Reset both file storage types (Cropped Blob and Direct Upload File)
    this.croppedBlob = null;
    this.rawFileToUpload = null;

    // 3. Clear the DOM file input value so the same image can be re-selected if needed
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
